// src/views/admin/ADImportEleves.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

// ─── COMPOSANTS ──────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ children, title, icon }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {title && (
        <div className="ch">
          <div className="ct">
            {icon && <i className={`ti ${icon}`} style={{ marginRight: 8 }} />}
            {title}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

// ─── CONSTANTES ──────────────────────────────────────────────────────────────
const GROUPS = ["Élève", "Père", "Mère", "Inscription"];

// Les clés = noms de colonnes canoniques attendus par le backend.
const FIELD_MAPPING = {
  // Élève
  matricule: { label: "Matricule (auto si vide)", group: "Élève", required: false, aliases: [] },
  nom: { label: "Nom", group: "Élève", required: true, aliases: [] },
  prenom: { label: "Prénom", group: "Élève", required: true, aliases: [] },
  genre: { label: "Genre (M/F)", group: "Élève", required: true, aliases: ["sexe"] },
  date_naissance: { label: "Date de naissance", group: "Élève", required: false, aliases: ["date de naissance", "date naissance"] },
  ne_vers: { label: "Né vers (oui/non)", group: "Élève", required: false, aliases: ["ne vers", "né vers"] },
  annee_naissance_approximative: { label: "Année de naissance (si né vers)", group: "Élève", required: false, aliases: ["annee de naissance", "année de naissance", "annee naissance"] },
  lieu_naissance: { label: "Lieu de naissance", group: "Élève", required: true, aliases: ["lieu de naissance", "lieu naissance"] },
  adresse: { label: "Adresse", group: "Élève", required: false, aliases: [] },
  nationalite: { label: "Nationalité", group: "Élève", required: false, aliases: ["nationalité"] },
  etat_sanitaire: { label: "État sanitaire", group: "Élève", required: false, aliases: ["état sanitaire", "etat sanitaire"] },
  situation_sociale: { label: "Situation sociale", group: "Élève", required: false, aliases: ["situation sociale"] },
  telephone_parent: { label: "Téléphone parent", group: "Élève", required: false, aliases: ["telephone", "téléphone", "téléphone parent", "telephone parent"] },
  // Père
  pere_nom: { label: "Père — nom", group: "Père", required: false, aliases: ["nom du pere", "nom du père", "nom pere"] },
  pere_prenom: { label: "Père — prénom", group: "Père", required: false, aliases: ["prenom du pere", "prénom du père", "prenom pere"] },
  pere_profession: { label: "Père — profession", group: "Père", required: false, aliases: ["profession pere", "profession du pere"] },
  pere_adresse: { label: "Père — adresse", group: "Père", required: false, aliases: ["adresse pere", "adresse du pere"] },
  pere_telephone: { label: "Père — téléphone", group: "Père", required: false, aliases: ["telephone pere", "téléphone père", "tel pere"] },
  // Mère
  mere_nom: { label: "Mère — nom", group: "Mère", required: false, aliases: ["nom de la mere", "nom de la mère", "nom mere"] },
  mere_prenom: { label: "Mère — prénom", group: "Mère", required: false, aliases: ["prenom de la mere", "prénom de la mère", "prenom mere"] },
  mere_profession: { label: "Mère — profession", group: "Mère", required: false, aliases: ["profession mere", "profession de la mere"] },
  mere_adresse: { label: "Mère — adresse", group: "Mère", required: false, aliases: ["adresse mere", "adresse de la mere"] },
  mere_telephone: { label: "Mère — téléphone", group: "Mère", required: false, aliases: ["telephone mere", "téléphone mère", "tel mere"] },
  // Inscription
  classe_nom: { label: "Classe", group: "Inscription", required: true, aliases: ["classe"] },
  type_eleve: { label: "Type d'élève", group: "Inscription", required: false, aliases: ["type eleve", "type élève"] },
  etablissement_origine: { label: "Établissement d'origine", group: "Inscription", required: false, aliases: ["etablissement origine", "établissement d'origine", "ecole d'origine"] },
  is_nouveau: { label: "Nouveau (oui/non, défaut oui)", group: "Inscription", required: false, aliases: ["nouveau"] },
  is_redoublant: { label: "Redoublant (oui/non)", group: "Inscription", required: false, aliases: ["redoublant"] },
  classe_redoublee: { label: "Classe redoublée", group: "Inscription", required: false, aliases: ["classe redoublee", "classe redoublée"] },
  date_inscription: { label: "Date d'inscription", group: "Inscription", required: false, aliases: ["date inscription", "date d'inscription"] },
};

const FIELD_KEYS = Object.keys(FIELD_MAPPING);
const BOOL_KEYS = ["ne_vers", "is_nouveau", "is_redoublant"];

const GENRE_MAPPING = {
  'M': 'M',
  'F': 'F',
  'MASCULIN': 'M',
  'FÉMININ': 'F',
  'FEMININ': 'F',
  'HOMME': 'M',
  'FEMME': 'F',
};

const TRUE_VALUES = ["oui", "o", "yes", "y", "true", "vrai", "1", "x"];
const FALSE_VALUES = ["non", "n", "no", "false", "faux", "0"];

// ─── UTILITAIRES ─────────────────────────────────────────────────────────────
const norm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s'’\-]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toList = (d) => (Array.isArray(d) ? d : d?.results ?? []);
const labelsOf = (data, key) =>
  toList(data).map(x => String(x?.[key] ?? "").trim()).filter(Boolean);

// Cellule Excel → texte. Les dates deviennent AAAA-MM-JJ (indépendant du format d'affichage Excel).
const cellToString = (v) => {
  if (v === undefined || v === null) return "";
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return "";
    const d = new Date(v.getTime() + 12 * 3600 * 1000); // +12h : évite les décalages de fuseau de SheetJS
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  }
  return String(v).trim();
};

// Retourne "AAAA-MM-JJ" ou null. Formats acceptés = ceux du backend.
const parseDate = (str) => {
  const s = String(str ?? "").trim();
  let y, m, d, match;
  if ((match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    [y, m, d] = [match[1], match[2], match[3]];
  } else if ((match = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/))) {
    [d, m, y] = [match[1], match[2], match[3]];
  } else return null;
  y = +y; m = +m; d = +d;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// undefined = vide, null = invalide, sinon true/false
const parseBool = (v) => {
  const s = norm(v);
  if (!s) return undefined;
  if (TRUE_VALUES.includes(s)) return true;
  if (FALSE_VALUES.includes(s)) return false;
  return null;
};

// refs.<x> = tableau de libellés autorisés, ou null si le référentiel n'a pas pu être chargé
// (dans ce cas on ne valide pas côté front, le backend tranchera).
const validateRow = (row, classes, refs) => {
  const errs = [];
  if (!row.nom) errs.push("Nom requis");
  if (!row.prenom) errs.push("Prénom requis");
  if (!row.lieu_naissance) errs.push("Lieu de naissance requis");

  const neVers = parseBool(row.ne_vers);
  if (neVers === null) errs.push(`ne_vers invalide "${row.ne_vers}" (oui/non)`);
  if (neVers) {
    const y = Number(row.annee_naissance_approximative);
    if (!Number.isInteger(y) || y < 1900 || y > new Date().getFullYear()) {
      errs.push("Année de naissance approximative requise et valide (né vers)");
    }
  } else if (!row.date_naissance) {
    errs.push("Date de naissance requise (ou né vers = oui + année)");
  } else if (!parseDate(row.date_naissance)) {
    errs.push(`Date invalide "${row.date_naissance}" (AAAA-MM-JJ ou JJ/MM/AAAA)`);
  }

  if (!row.genre) errs.push("Genre requis (M ou F)");
  else if (!GENRE_MAPPING[row.genre.toUpperCase().trim()]) errs.push(`Genre invalide "${row.genre}" (M ou F)`);

  if (!row.classe_nom) errs.push("Classe requise");
  else if (!classes.some(c => c.nom.toLowerCase().trim() === row.classe_nom.toLowerCase().trim())) {
    errs.push(`Classe "${row.classe_nom}" introuvable dans l'année active`);
  }

  const checkRef = (value, list, label) => {
    if (!value || !Array.isArray(list)) return;
    if (!list.some(x => x.toLowerCase().trim() === value.toLowerCase().trim())) {
      errs.push(`${label} "${value}" introuvable (voir feuille « Valeurs autorisées »)`);
    }
  };
  checkRef(row.nationalite, refs.nationalites, "Nationalité");
  checkRef(row.etat_sanitaire, refs.etats, "État sanitaire");
  checkRef(row.situation_sociale, refs.situations, "Situation sociale");
  checkRef(row.type_eleve, refs.types, "Type d'élève");

  if (parseBool(row.is_nouveau) === null) errs.push(`Nouveau invalide "${row.is_nouveau}" (oui/non)`);
  const redoublant = parseBool(row.is_redoublant);
  if (redoublant === null) errs.push(`Redoublant invalide "${row.is_redoublant}" (oui/non)`);
  if (redoublant && !row.classe_redoublee) errs.push("Classe redoublée requise pour un redoublant");

  if (row.date_inscription && !parseDate(row.date_inscription)) {
    errs.push(`Date d'inscription invalide "${row.date_inscription}"`);
  }
  return errs;
};

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────
export default function ADImportEleves({ slug }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [anneeActive, setAnneeActive] = useState(null);
  const [classes, setClasses] = useState([]);
  const [refs, setRefs] = useState({ types: null, nationalites: null, etats: null, situations: null });
  const [file, setFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [creerComptesParents, setCreerComptesParents] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);

  const toast = useToastHook();
  const {
    getAnneesScolaires, getClasses, importEleves,
    getTypesEleves, getNationalites, getEtatsSanitaires, getSituationsSociales,
  } = useAdminService();

  // ─── CHARGEMENT ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (slug) loadData();
  }, [slug]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Les référentiels sont tolérants à l'échec (null) : un échec n'empêche pas l'import,
      // il désactive juste la validation front de ce référentiel.
      const [anneesData, classesData, types, nats, etats, situs] = await Promise.all([
        getAnneesScolaires(slug),
        getClasses(slug),
        getTypesEleves(slug).catch(() => null),
        getNationalites(slug).catch(() => null),
        getEtatsSanitaires(slug).catch(() => null),
        getSituationsSociales(slug).catch(() => null),
      ]);

      const active = anneesData.find(a => a.actif);
      if (!active) {
        toast.error("Aucune année scolaire active. Veuillez en activer une.");
        setLoading(false);
        return;
      }

      setAnneeActive(active);
      setClasses(classesData.filter(c => c.annee_scolaire === active.id));
      setRefs({
        types: types ? labelsOf(types, "libelle") : null,
        nationalites: nats ? labelsOf(nats, "nom") : null,
        etats: etats ? labelsOf(etats, "libelle") : null,
        situations: situs ? labelsOf(situs, "libelle") : null,
      });

      if (!types || !nats || !etats || !situs) {
        toast.warning("Certains référentiels n'ont pas pu être chargés : ils seront validés par le serveur.");
      }
    } catch (error) {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  // Aperçu + validation recalculés dès que les lignes, le mapping, les classes ou les référentiels changent
  useEffect(() => {
    if (!rawRows.length) {
      setPreviewData([]);
      setValidationErrors([]);
      return;
    }
    const preview = convertToPreview(rawRows, mapping);
    setPreviewData(preview);
    setValidationErrors(validateData(preview, mapping));
  }, [rawRows, mapping, classes, refs]);

  // ─── GESTION FICHIER ──────────────────────────────────────────────────────
  const loadFile = (selectedFile) => {
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf(".")).toLowerCase();
    if (![".xlsx", ".xls", ".csv"].includes(ext)) {
      toast.error("Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV");
      return;
    }
    setFile(selectedFile);
    readExcelFile(selectedFile);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) loadFile(selectedFile);
    e.target.value = "";
  };

  const readExcelFile = (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array", cellDates: true });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "", raw: true });

        if (jsonData.length < 2) {
          toast.error("Le fichier est vide ou ne contient pas d'en-têtes");
          return;
        }

        const headers = jsonData[0].map((h, i) => {
          const s = cellToString(h);
          return s || `Colonne ${i + 1}`;
        });
        setExcelHeaders(headers);
        setMapping(detectAutoMapping(headers));
        setRawRows(jsonData.slice(1));
      } catch (error) {
        toast.error("Impossible de lire le fichier Excel");
      }
    };
    reader.readAsArrayBuffer(f);
  };

  // Correspondance EXACTE (après normalisation) : nom canonique ou alias. Pas de repli par position.
  const detectAutoMapping = (headers) => {
    const map = {};
    const normHeaders = headers.map(norm);
    FIELD_KEYS.forEach((key) => {
      const candidates = [key, ...FIELD_MAPPING[key].aliases].map(norm);
      const idx = normHeaders.findIndex(h => candidates.includes(h));
      if (idx !== -1) map[key] = idx;
    });
    return map;
  };

  const updateMapping = (key, value) => {
    setMapping(prev => {
      const next = { ...prev };
      if (value === "") delete next[key];
      else next[key] = Number(value);
      return next;
    });
  };

  const convertToPreview = (rows, map) => {
    const out = [];
    rows.forEach((row, i) => {
      if (!row.some(cell => cellToString(cell) !== "")) return;
      const obj = { rowIndex: i + 2 };
      FIELD_KEYS.forEach(k => {
        obj[k] = map[k] === undefined ? "" : cellToString(row[map[k]]);
      });
      out.push(obj);
    });
    return out;
  };

  const validateData = (data, map) => {
    const errors = [];
    FIELD_KEYS.forEach(k => {
      if (FIELD_MAPPING[k].required && map[k] === undefined) {
        errors.push(`Colonne obligatoire non mappée : ${FIELD_MAPPING[k].label}`);
      }
    });
    data.forEach(row => {
      validateRow(row, classes, refs).forEach(msg => errors.push(`Ligne ${row.rowIndex}: ${msg}`));
    });
    return errors;
  };

  // Fichier normalisé : colonnes canoniques + dates ISO + booléens oui/non.
  // Le mapping choisi ici est donc réellement celui utilisé par le backend,
  // et .xls / .csv passent aussi (le backend ne lit que .xlsx).
  const buildNormalizedFile = () => {
    const rows = previewData.map(r => {
      const o = {};
      FIELD_KEYS.forEach(k => { o[k] = r[k] ?? ""; });
      o.date_naissance = parseDate(r.date_naissance) || "";
      o.date_inscription = parseDate(r.date_inscription) || "";
      o.genre = GENRE_MAPPING[(r.genre || "").toUpperCase().trim()] || "";
      BOOL_KEYS.forEach(k => {
        const b = parseBool(r[k]);
        o[k] = b === undefined ? "" : b ? "oui" : "non";
      });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(rows, { header: FIELD_KEYS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Eleves");
    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    return new File([buf], "import_eleves.xlsx", {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  // ─── TEMPLATE ─────────────────────────────────────────────────────────────
  const downloadTemplate = () => {
    if (!anneeActive || classes.length === 0) {
      toast.warning("Impossible de télécharger le template, données non chargées");
      return;
    }

    // Exemples construits avec de VRAIES valeurs de l'établissement (donc importables tels quels)
    const first = (list, i = 0) => (Array.isArray(list) && list.length ? list[Math.min(i, list.length - 1)] : "");
    const classe1 = classes[0].nom;
    const classe2 = classes[Math.min(1, classes.length - 1)].nom;

    const examples = [
      {
        matricule: "", nom: "OUEDRAOGO", prenom: "Abdoulaye", genre: "M", date_naissance: "2015-05-15",
        lieu_naissance: "Ouagadougou", adresse: "Secteur 12", nationalite: first(refs.nationalites),
        etat_sanitaire: first(refs.etats), situation_sociale: first(refs.situations),
        telephone_parent: "70234567",
        pere_nom: "OUEDRAOGO", pere_prenom: "Issa", pere_profession: "Commerçant", pere_telephone: "70234567",
        mere_nom: "SAWADOGO", mere_prenom: "Awa", mere_profession: "Enseignante",
        classe_nom: classe1, type_eleve: first(refs.types), is_nouveau: "oui", is_redoublant: "non",
      },
      {
        matricule: "", nom: "TRAORE", prenom: "Fatimata", genre: "F", date_naissance: "",
        ne_vers: "oui", annee_naissance_approximative: "2014", lieu_naissance: "Bobo-Dioulasso",
        nationalite: first(refs.nationalites), telephone_parent: "71987654",
        classe_nom: classe2, type_eleve: first(refs.types, 1),
        etablissement_origine: "École Sainte-Marie", is_nouveau: "non",
        is_redoublant: "oui", classe_redoublee: classe2,
      },
    ];

    const wsData = XLSX.utils.aoa_to_sheet([
      FIELD_KEYS,
      ...examples.map(ex => FIELD_KEYS.map(k => ex[k] ?? "")),
    ]);
    wsData["!cols"] = FIELD_KEYS.map(() => ({ wch: 20 }));

    // Feuille des valeurs autorisées : une colonne par référentiel, à recopier EXACTEMENT
    const cols = [
      { title: `Classes (${anneeActive.nom})`, values: classes.map(c => c.nom) },
      { title: "Types d'élève", values: refs.types || [] },
      { title: "Nationalités", values: refs.nationalites || [] },
      { title: "États sanitaires", values: refs.etats || [] },
      { title: "Situations sociales", values: refs.situations || [] },
      { title: "Genre", values: ["M", "F"] },
      { title: "Oui / Non", values: ["oui", "non"] },
    ];
    const maxLen = Math.max(...cols.map(c => c.values.length));
    const listRows = [
      cols.map(c => c.title),
      ...Array.from({ length: maxLen }, (_, i) => cols.map(c => c.values[i] ?? "")),
    ];
    const wsListes = XLSX.utils.aoa_to_sheet(listRows);
    wsListes["!cols"] = cols.map(() => ({ wch: 28 }));

    const aide = [
      ["AIDE"],
      [],
      ["Colonnes obligatoires", "nom, prenom, genre, lieu_naissance, classe_nom, + date_naissance (ou ne_vers = oui + annee_naissance_approximative)"],
      ["matricule", "Laisser vide pour une génération automatique"],
      ["classe_nom / type_eleve / nationalite / etat_sanitaire / situation_sociale", "Recopier exactement une valeur de la feuille « Valeurs autorisées »"],
      ["ne_vers / is_nouveau / is_redoublant", "oui ou non (is_nouveau = oui par défaut)"],
      ["Dates", "AAAA-MM-JJ ou JJ/MM/AAAA"],
      ["classe_redoublee", "Nom de la classe redoublée (recherchée d'abord dans les années précédentes)"],
      ["pere_* / mere_*", "La fiche tuteur n'est créée que si au moins un champ est renseigné"],
      ["Créer les comptes parents (case à cocher)", "Un compte PARENT par téléphone_parent sans compte existant. Priorité de rattachement : père si son téléphone correspond, sinon mère si le sien correspond, sinon père, sinon mère."],
    ];
    const wsAide = XLSX.utils.aoa_to_sheet(aide);
    wsAide["!cols"] = [{ wch: 60 }, { wch: 90 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsData, "Template Élèves");
    XLSX.utils.book_append_sheet(wb, wsListes, "Valeurs autorisées");
    XLSX.utils.book_append_sheet(wb, wsAide, "Aide");
    XLSX.writeFile(wb, `Template_Import_Eleves_${anneeActive.nom}.xlsx`);
    toast.success("Template téléchargé avec succès");
  };

  // ─── IMPORT ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file) {
      toast.error("Veuillez sélectionner un fichier Excel");
      return;
    }
    if (!anneeActive) {
      toast.error("Aucune année scolaire active");
      return;
    }
    if (validationErrors.length > 0) {
      toast.error("Des erreurs de validation doivent être corrigées");
      return;
    }

    setUploading(true);
    try {
      // importEleves(slug, file, extra) : extra est fusionné dans le FormData envoyé au backend.
      // Vérifie que adminService.importEleves accepte bien ce 3e paramètre — sinon adapte-le
      // sur ce modèle :
      //   const importEleves = async (slug, file, extra = {}) => {
      //     const formData = new FormData();
      //     formData.append("file", file);
      //     Object.entries(extra).forEach(([k, v]) => formData.append(k, v));
      //     const response = await axiosInstance.post(tenantURL(slug, "eleves/import/"), formData, {
      //       headers: { "Content-Type": "multipart/form-data" },
      //     });
      //     return response.data;
      //   };
      const result = await importEleves(slug, buildNormalizedFile(), {
        creer_comptes_parents: creerComptesParents ? "oui" : "non",
      });
      setImportResult(result);
      setShowResultModal(true);

      if (result.nb_erreurs === 0) {
        toast.success(`${result.nb_eleves_crees} élèves importés avec succès`);
      } else {
        toast.warning(`${result.nb_eleves_crees} importés, ${result.nb_erreurs} erreurs`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de l'import");
    } finally {
      setUploading(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setRawRows([]);
    setPreviewData([]);
    setValidationErrors([]);
    setExcelHeaders([]);
    setMapping({});
    setImportResult(null);
  };

  // ─── RENDU ──────────────────────────────────────────────────────────────────
  if (loading) return <Loader />;

  return (
    <div>
      {/* En-tête avec bouton retour */}
      <div className="sb2" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="bsec" onClick={() => navigate(`/${slug}/admin/eleves`)}>
            <i className="ti ti-arrow-left" /> Retour
          </button>
          <h2 style={{ margin: 0, color: "var(--pr)" }}>
            <i className="ti ti-file-spreadsheet" style={{ marginRight: 8 }} />
            Import des élèves depuis Excel
          </h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="bsec" onClick={downloadTemplate} disabled={!anneeActive}>
            <i className="ti ti-download" /> Télécharger le template
          </button>
        </div>
      </div>

      {/* Année active */}
      {anneeActive && (
        <div className="active-year-banner">
          <i className="ti ti-calendar" />
          <span>Année active : <strong>{anneeActive.nom}</strong></span>
          <span style={{ marginLeft: 12, color: "var(--m)" }}>
            Classes disponibles : {classes.length}
          </span>
        </div>
      )}

      {!anneeActive && (
        <div className="alert-banner">
          <i className="ti ti-alert-triangle" />
          <span>Aucune année scolaire active. Veuillez en activer une.</span>
        </div>
      )}

      {/* Zone d'upload */}
      <Card title="Importer le fichier Excel" icon="ti-upload">
        <div
          className="upload-area"
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = "var(--pr)";
            e.currentTarget.style.background = "var(--pr-bg)";
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = "var(--b)";
            e.currentTarget.style.background = "transparent";
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = "var(--b)";
            e.currentTarget.style.background = "transparent";
            if (e.dataTransfer.files.length) loadFile(e.dataTransfer.files[0]);
          }}
        >
          <div style={{ fontSize: 48, color: "var(--pr)", marginBottom: 12 }}>
            <i className="ti ti-file-spreadsheet" />
          </div>
          <h4 style={{ color: "var(--t)", marginBottom: 8 }}>
            {file ? file.name : "Déposer votre fichier Excel ici"}
          </h4>
          <p style={{ color: "var(--m)", marginBottom: 16, fontSize: 14 }}>
            ou cliquez pour sélectionner un fichier (.xlsx, .xls, .csv)
          </p>
          <input
            id="file-upload"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <label htmlFor="file-upload" className="bprim" style={{ cursor: "pointer" }}>
            <i className="ti ti-folder-open" /> Parcourir
          </label>
          {file && (
            <div style={{ marginTop: 12, fontSize: 13, color: "var(--m)" }}>
              <i className="ti ti-file" /> {file.name} ({(file.size / 1024).toFixed(0)} Ko)
            </div>
          )}
        </div>
      </Card>

      {/* Mapping et Aperçu */}
      {file && rawRows.length > 0 && (
        <>
          <Card title="Correspondance des colonnes" icon="ti-list">
            <p style={{ color: "var(--m)", fontSize: 13, marginTop: 0 }}>
              Détection automatique par nom de colonne. Ajustez au besoin : les colonnes non mappées restent vides.
            </p>
            {GROUPS.map(group => (
              <div key={group} style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 600, color: "var(--pr)", marginBottom: 6 }}>{group}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {FIELD_KEYS.filter(k => FIELD_MAPPING[k].group === group).map(key => {
                    const field = FIELD_MAPPING[key];
                    return (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                        <span style={{ color: "var(--m)", minWidth: 170 }}>
                          {field.label}{field.required && <span style={{ color: "var(--rd)" }}> *</span>}
                        </span>
                        <select
                          value={mapping[key] ?? ""}
                          onChange={(e) => updateMapping(key, e.target.value)}
                          style={{ flex: 1, minWidth: 0, padding: "4px 6px" }}
                        >
                          <option value="">— Non mappé —</option>
                          {excelHeaders.map((h, i) => (
                            <option key={i} value={i}>{i + 1}. {h}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </Card>

          {/* Erreurs de validation */}
          {validationErrors.length > 0 && (
            <div className="alert-banner" style={{ borderColor: "var(--rd)", background: "var(--rd-bg)", color: "var(--rd)" }}>
              <i className="ti ti-alert-circle" />
              <span>
                <strong>{validationErrors.length} erreur(s) de validation :</strong>
                <div style={{ maxHeight: 150, overflowY: "auto", marginTop: 6, fontSize: 13 }}>
                  {validationErrors.slice(0, 30).map((err, i) => (
                    <div key={i}>• {err}</div>
                  ))}
                  {validationErrors.length > 30 && (
                    <div>... et {validationErrors.length - 30} autres erreurs</div>
                  )}
                </div>
              </span>
            </div>
          )}

          {/* Aperçu des données */}
          <Card title={`Aperçu des données (${previewData.length} lignes)`} icon="ti-eye">
            <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
              <table className="tbl">
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "var(--s)", zIndex: 10 }}>
                    <th>#</th>
                    <th>Matricule</th>
                    <th>Nom & prénom</th>
                    <th>Naissance</th>
                    <th>Genre</th>
                    <th>Classe</th>
                    <th>Type</th>
                    <th>Redoublant</th>
                    <th>Père</th>
                    <th>Mère</th>
                    <th>Tél. parent</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 50).map((row, i) => {
                    const errs = validateRow(row, classes, refs);
                    const ok = errs.length === 0;
                    const naissance = parseBool(row.ne_vers)
                      ? `vers ${row.annee_naissance_approximative || "?"}`
                      : row.date_naissance || "—";
                    const red = parseBool(row.is_redoublant);
                    return (
                      <tr key={i}>
                        <td style={{ color: "var(--m)", fontSize: 12 }}>{row.rowIndex}</td>
                        <td>{row.matricule || <span style={{ color: "var(--m)" }}>auto</span>}</td>
                        <td>{`${row.nom} ${row.prenom}`.trim() || "—"}</td>
                        <td>{naissance}</td>
                        <td>{row.genre || "—"}</td>
                        <td>{row.classe_nom || "—"}</td>
                        <td>{row.type_eleve || "—"}</td>
                        <td>{red ? `Oui (${row.classe_redoublee || "?"})` : "—"}</td>
                        <td>{`${row.pere_nom} ${row.pere_prenom}`.trim() || "—"}</td>
                        <td>{`${row.mere_nom} ${row.mere_prenom}`.trim() || "—"}</td>
                        <td>{row.telephone_parent || "—"}</td>
                        <td
                          title={errs.join("\n")}
                          style={{ color: ok ? "var(--gn)" : "var(--rd)", fontSize: 12 }}
                        >
                          {ok ? "✓" : `❌ ${errs.length}`}
                        </td>
                      </tr>
                    );
                  })}
                  {previewData.length > 50 && (
                    <tr><td colSpan="12" style={{ textAlign: "center", color: "var(--h)", padding: 12 }}>
                      ... et {previewData.length - 50} autres lignes
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Option : création des comptes parents */}
          <Card title="Comptes parents" icon="ti-user-plus">
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={creerComptesParents}
                onChange={(e) => setCreerComptesParents(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                Créer les comptes parents manquants
                <div style={{ color: "var(--m)", fontSize: 12, marginTop: 2 }}>
                  Un compte PARENT est créé pour chaque téléphone_parent n'ayant pas déjà de compte
                  dans cet établissement, avec un mot de passe aléatoire et un lien d'activation
                  envoyé par SMS. Le tuteur (père ou mère) dont le téléphone correspond est lié en
                  priorité ; sinon le père, sinon la mère.
                </div>
              </span>
            </label>
          </Card>

          {/* Boutons d'action */}
          <div className="factions" style={{ justifyContent: "center", gap: 16 }}>
            <button className="bsec" onClick={resetImport}>
              <i className="ti ti-refresh" /> Réinitialiser
            </button>
            <button
              className="bprim"
              onClick={handleImport}
              disabled={uploading || validationErrors.length > 0}
            >
              {uploading ? (
                <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Import en cours...</>
              ) : (
                <><i className="ti ti-upload" /> Importer les élèves</>
              )}
            </button>
          </div>
        </>
      )}

      {/* Instructions */}
      <Card title="Instructions" icon="ti-info-circle">
        <ol style={{ color: "var(--m)", lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
          <li>Téléchargez le template Excel (feuille « Aide » incluse) pour voir le format attendu</li>
          <li>Remplissez-le : élève, père, mère, inscription (matricule vide = généré automatiquement)</li>
          <li>Pour classe, type d'élève, nationalité, état sanitaire et situation sociale : recopiez exactement une valeur de la feuille « Valeurs autorisées »</li>
          <li>Importez votre fichier, vérifiez la correspondance des colonnes et corrigez les erreurs de l'aperçu</li>
          <li>Cochez « Créer les comptes parents manquants » si besoin, puis cliquez sur "Importer les élèves"</li>
        </ol>
        <div style={{
          marginTop: 12,
          padding: 12,
          background: "var(--gn-bg)",
          borderRadius: 6,
          fontSize: 13,
          color: "var(--gn)"
        }}>
          <i className="ti ti-info-circle" /> Les élèves seront automatiquement inscrits dans l'année active <strong>{anneeActive?.nom}</strong>.
        </div>
      </Card>

      {/* Modal Résultat */}
      {showResultModal && importResult && (
        <Modal title="Résultat de l'import" onClose={() => { setShowResultModal(false); resetImport(); }} maxWidth={600}>
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div className="card" style={{ padding: 16, textAlign: "center", borderColor: "var(--gn)" }}>
                <div style={{ fontSize: 32, color: "var(--gn)" }}>
                  <i className="ti ti-check" />
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--gn)" }}>
                  {importResult.nb_eleves_crees || 0}
                </div>
                <div style={{ fontSize: 13, color: "var(--m)" }}>Importés avec succès</div>
              </div>
              <div className="card" style={{ padding: 16, textAlign: "center", borderColor: "var(--rd)" }}>
                <div style={{ fontSize: 32, color: "var(--rd)" }}>
                  <i className="ti ti-x" />
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--rd)" }}>
                  {importResult.nb_erreurs || 0}
                </div>
                <div style={{ fontSize: 13, color: "var(--m)" }}>Erreurs</div>
              </div>
            </div>

            {creerComptesParents && (
              <div style={{ marginBottom: 16, fontSize: 13, color: "var(--m)" }}>
                <i className="ti ti-user-plus" /> Comptes parents créés : <strong>{importResult.nb_comptes_parents_crees || 0}</strong>
              </div>
            )}

            {importResult.erreurs && importResult.erreurs.length > 0 && (
              <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 13 }}>
                {importResult.erreurs.map((err, i) => (
                  <div key={i} style={{
                    padding: 6,
                    borderBottom: "0.5px solid var(--b)",
                    color: "var(--rd)"
                  }}>
                    <strong>Ligne {err.ligne ?? "?"} · {err.matricule || "N/A"}</strong> — {String(err.errors)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="factions">
            <button className="bprim" onClick={() => { setShowResultModal(false); resetImport(); }}>
              <i className="ti ti-check" /> Terminer
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin { 
          0% { transform: rotate(0deg); } 
          100% { transform: rotate(360deg); } 
        }

        .upload-area {
          border: 2px dashed var(--b);
          border-radius: 10px;
          padding: 40px 20px;
          text-align: center;
          transition: all 0.3s;
          cursor: pointer;
        }

        .upload-area:hover {
          border-color: var(--pr);
          background: var(--pr-bg);
        }

        .active-year-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          background: var(--gn-bg);
          border: 2px solid var(--gn);
          border-radius: 8px;
          color: var(--gn);
          font-size: 14px;
          margin-bottom: 14px;
        }

        .active-year-banner i { font-size: 20px; }
        .active-year-banner strong { color: var(--gn); }

        .alert-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          background: var(--am-bg);
          border: 2px solid var(--am);
          border-radius: 8px;
          color: var(--am);
          font-size: 14px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        .alert-banner i { font-size: 22px; flex-shrink: 0; }
        .alert-banner span { flex: 1; }
      `}</style>
    </div>
  );
}